// sections.jsx — About, Focus, Projects, Notes, StatusLog, Contact, Footer

import React from "react";
import { PresenceBadge, useLive, usePresence } from "./live.jsx";
const { useState: useStateSec, useEffect: useEffectSec } = React;

// ── Shared contact fetch — one request, shared across StatusLog, Contact and Footer ──
// Everything here comes from ContactInfoService on the server, which is the same source the Razor
// footer and the JSON-LD block read. Nothing about a contact address is spelled out in this file.
const settingsPromise = fetch('/api/settings').then(r => r.json()).catch(() => ({}));

function useSettings() {
  const [s, setS] = useStateSec({ email: '', links: [] });
  useEffectSec(() => {
    settingsPromise.then(d => setS({ email: d.email || '', links: d.links || [] }));
  }, []);
  return s;
}

// Brand logos arrive as a ready-made cdn.simpleicons.org URL so the tint hex lives server-side in
// exactly one place; everything else is a lucide mark.
//
// The lucide branch goes through innerHTML for the same reason SlIcon does below: lucide swaps
// every <i data-lucide> for an <svg> via replaceChild, detaching the node React created. Keeping
// it out of reconciliation means a re-ordered or re-iconed link still repaints.
function LinkIcon({ link }) {
  // simpleicons withdraws slugs occasionally — LinkedIn is one — and a withdrawn one 404s.
  // Hiding the image degrades to a plain text link instead of a broken-image glyph.
  if (link.iconUrl) return <img src={link.iconUrl} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} />;
  if (!link.iconName) return null;
  return <span className="li-ico" dangerouslySetInnerHTML={{ __html: '<i data-lucide="' + link.iconName + '"></i>' }} />;
}

// One <a> per row. className differs between the contact section and the footer, nothing else does.
function ContactLinkAnchor({ link, className }) {
  return (
    <a className={className} href={link.url}
       target={link.isExternal ? '_blank' : undefined}
       rel={link.isExternal ? 'noopener' : undefined}>
      <LinkIcon link={link} /> {link.label}
    </a>
  );
}

// consistent, chapter-numbered section header — single-author rhythm
function SectionHead({ ch, label, title, lead }) {
  return (
    <div className="sec-head">
      <div className="sec-meta">
        <span className="eyebrow chapter"><span className="ch">{ch}</span>{label}</span>
        <span className="sec-rule"></span>
      </div>
      <h2 className="sec-title">{title}</h2>
      {lead && <p className="sec-lead">{lead}</p>}
    </div>
  );
}

function About() {
  const [lately, setLately] = useStateSec([]);
  const [contentHtml, setContentHtml] = useStateSec('');

  useEffectSec(() => {
    fetch('/api/lately-items').then(r => r.json()).then(setLately).catch(() => {});
    // The same SiteSettings.AboutContent the /about page renders. The prose used to be
    // hardcoded here, which meant the two could say different things; now there is one
    // admin-editable source and neither can drift.
    fetch('/api/about').then(r => r.json()).then(d => setContentHtml(d.contentHtml || '')).catch(() => {});
  }, []);

  return (
    <section id="about" className="wrap reveal">
      <div className="about-grid">
        <div>
          <div className="sec-meta">
            <span className="eyebrow chapter"><span className="ch">01</span>about</span>
            <span className="sec-rule"></span>
          </div>
          <h2 className="sec-title">I turn mess into <span className="accent">method</span>.</h2>
          {/* Prose comes from the database; the principles below do not. They are a fixed
              three-item set with icons, not something worth round-tripping through Markdown. */}
          <div className="about-body" dangerouslySetInnerHTML={{ __html: contentHtml }} />
          <div className="about-principles">
            <span className="principle"><i data-lucide="check"></i> test it before you trust it</span>
            <span className="principle"><i data-lucide="check"></i> automate the boring</span>
            <span className="principle"><i data-lucide="check"></i> sustainable &gt; heroic</span>
          </div>
        </div>
        <div className="card about-card">
          <div className="ac-head">~/whoami</div>
          <div className="kv">
            <div className="row"><span className="k">role</span><span className="v">QA <b>Architect</b> | Developer</span></div>
            <div className="row"><span className="k">builds</span><span className="v"><b>SortiiiQ</b>, MyMoney &amp; Oria</span></div>
            <div className="row"><span className="k">writes</span><span className="v">QA architecture, automation &amp; AI skills</span></div>
            <div className="row"><span className="k">thinks_about</span><span className="v">better solutions to annoying problems</span></div>
            <div className="row"><span className="k">mind</span><span className="v"><b>focus-friendly</b>, sustainable and open-minded</span></div>
            <div className="row"><span className="k">location</span><span className="v">Zlín + Prague, CZ</span></div>
            <div className="row"><span className="k">status</span><span className="v">onboarding into a seriously cool new job</span></div>
          </div>
        </div>
      </div>
      {lately.length > 0 && (
        <div className="about-lately">
          <div className="ll">// lately — where my focus goes</div>
          <div className="lately-grid">
            {lately.map((item, i) => (
              <div key={i} className="lately-item">
                <i data-lucide={item.iconName}></i>
                <span dangerouslySetInnerHTML={{ __html: item.textHtml }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const FOCUS = [
  { num: "01", icon: "bug", title: "Software Quality & QA", desc: "Testing strategy, critical thinking, and finding what breaks before a user ever does.", tags: ["testing", "strategy", "automation"] },
  { num: "02", icon: "terminal", title: "Tools & Projects", desc: "I build tools, experiments, and small systems that solve real problems — and I enjoy seeing them through.", tags: ["building", "experiments", "systems"] },
  { num: "03", icon: "brain-circuit", title: "Sustainable Work & Mind", desc: "Focus-friendly systems, burnout prevention, and working with my own mind — a sustainable pace over heroics.", tags: ["focus", "anti-burnout", "calm"] },
];

function Focus() {
  return (
    <section id="focus" className="wrap reveal">
      <SectionHead ch="02" label="focus"
        title="Three pillars I build on."
        lead="Technical depth, my own tools, and a healthy relationship with work — together they hold the whole thing up." />
      <div className="focus-grid">
        {FOCUS.map((f) => (
          <div key={f.title} className="card focus-card">
            <span className="num">{f.num}</span>
            <div className="focus-ico"><i data-lucide={f.icon}></i></div>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
            <div className="focus-tags">{f.tags.map((t) => <span key={t} className="mini-tag">{t}</span>)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Projects — fetched from /api/projects ─────────────────────────────────
function Projects() {
  const [projects, setProjects] = useStateSec([]);

  useEffectSec(() => {
    fetch('/api/projects').then(r => r.json()).then(setProjects).catch(() => {});
  }, []);

  const STATUS = {
    active:   ['active', ''],
    idea:     ['wip', 'wip'],
    archived: ['archived', 'archived'],
  };

  return (
    <section id="projects" className="wrap reveal">
      <SectionHead ch="03" label="selected projects"
        title="Things I've built."
        lead="Tools and systems, not a portfolio for show — each one exists because something was harder than it needed to be." />
      <div className="proj-grid">
        {projects.map((p, i) => {
          const [statusLabel, cls] = STATUS[p.status] || ['', ''];
          const lbl = p.isOpenSource ? 'open source' : statusLabel;
          return (
            <a key={p.slug} href={'/notes/' + p.slug}
               className={'card proj-card' + (i === 0 ? ' featured' : '')}>
              <span className="glowedge"></span>
              <div className="proj-visual">
                <span className="proj-cap"><span className="dot"></span>{p.title.toLowerCase()}</span>
                {/* The <image-slot> fallback that used to live here was a prototype scaffold: it
                    fetched a .image-slots.state.json sidecar that 404s on this site and wrote
                    through a window.omelette bridge that does not exist here, at the cost of
                    31 KiB of parser-blocking script on every home page load. .proj-visual already
                    paints a branded gradient and .proj-cap already labels the card, so a project
                    without a screenshot needs no element at all. */}
                {/* src/srcset/large all come from /api/projects, which resolves them through
                    IImageVariants. Rebuilding the "-1200.webp" convention here would be a second
                    implementation of it, wrong first for the images that have no variants yet. */}
                {p.imagePath && (
                  <img className="proj-shot"
                       src={p.imageSrc || p.imagePath}
                       srcSet={p.imageSrcset || undefined}
                       sizes={p.imageSrcset ? '(max-width: 700px) 100vw, 420px' : undefined}
                       alt=""
                       loading="lazy"
                       decoding="async"
                       data-lightbox={p.imageLarge || p.imagePath} />
                )}
              </div>
              <div className="proj-body">
                <div className="proj-top">
                  <span className="proj-name">{p.title}</span>
                  <span className={'proj-status ' + cls}>{lbl}</span>
                </div>
                <p className="proj-desc">{p.shortDescription}</p>
                <div className="proj-foot">
                  <span className="proj-tags">
                    {(p.tags || '').split(',').filter(t => t.trim()).map(t => (
                      <span key={t.trim()} className="mini-tag">{t.trim()}</span>
                    ))}
                  </span>
                  <span className="proj-cta">read case study <i data-lucide="arrow-up-right"></i></span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

// ── Notes — fetched from /api/notes ────────────────────────────────────
function Notes() {
  const [notes, setNotes] = useStateSec([]);
  const [active, setActive] = useStateSec('all');
  // Separate from notes.length so an in-flight fetch is not mistaken for an empty site —
  // otherwise the "nothing published" line flashes on every load before the data lands.
  const [loaded, setLoaded] = useStateSec(false);

  useEffectSec(() => {
    fetch('/api/notes')
      .then(r => r.json())
      .then(d => { setNotes(d); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m] = d.split('-');
    return y + ' · ' + m;
  };

  // What a note is, straight off the Kind column — the same value /notes and the reading page
  // badge from. It used to be guessed from the word "case" in the category, which the author could
  // neither choose nor undo.
  const KIND_LABEL = { 'case-study': 'case study', project: 'project', article: 'article', note: 'note' };
  const kindOf = (n) => (n.kind || 'note');
  const kindLabel = (n) => KIND_LABEL[kindOf(n)] || 'note';
  const kindClass = (n) => 'note-card-kind' + (kindOf(n) === 'case-study' ? ' cs' : '');

  const filters = ['all', ...[...new Set(
    notes.flatMap(n => [
      kindOf(n) === 'case-study' ? 'case-study' : null,
      ...(n.tags || '').split(',').map(t => t.trim()).filter(Boolean),
    ].filter(Boolean))
  )]];

  const shown = active === 'all' ? notes
    : notes.filter(n =>
        active === 'case-study'
          ? kindOf(n) === 'case-study'
          : (n.tags || '').split(',').map(t => t.trim()).includes(active)
      );

  // The grid is two columns and the lead card spans both, so the cards after it pair up only when
  // there is an even number of them: an even total left the last one alone beside a hole, which is
  // exactly what two published notes looked like. Odd lists lead with a wide card, even lists are a
  // plain grid of tiles. A filter has no lead — "newest" means newest, not newest matching — so
  // there the last card widens instead.
  const leads = active === 'all' && shown.length % 2 === 1;
  const tailIndex = !leads && shown.length % 2 === 1 ? shown.length - 1 : -1;

  const head = (
    <SectionHead ch="04" label="notes & case studies"
      title="Notes & case studies."
      lead="How I think, and what actually happened when I built it — testing, tools, systems, and a sustainable mind." />
  );

  // A section header above an empty grid, with a lone "all" chip under it, reads as a fetch
  // that failed rather than as a site with nothing published yet. Say the true thing instead.
  if (loaded && notes.length === 0) {
    return (
      <section id="notes" className="wrap reveal">
        {head}
        <p className="notes-empty">First notes are being written — nothing published yet.</p>
      </section>
    );
  }

  return (
    <section id="notes" className="wrap reveal">
      {head}
      <div className="note-card-filters">
        {filters.map(f => (
          <button key={f} className={'filter-chip' + (active === f ? ' active' : '')}
            onClick={() => setActive(f)}>{f === 'case-study' ? 'case study' : f}</button>
        ))}
      </div>
      <div className="note-card-grid">
        {shown.map((n, i) => {
          const tags = (n.tags || '').split(',').map(t => t.trim()).filter(Boolean);
          if (leads && i === 0) {
            return (
              <a key={n.slug} className="card note-card cs-wide" href={'/notes/' + n.slug}>
                <div className="cs-body">
                  <div className="note-card-top">
                    <span className={kindClass(n)}>{kindLabel(n)}</span>
                    <span className="note-card-date">{formatDate(n.publishedAt)}</span>
                  </div>
                  <h3>{n.title}</h3>
                  <p>{n.excerpt}</p>
                  <div className="note-card-foot cs-foot">
                    <span className="note-card-read"><i data-lucide="clock"></i> {n.readMinutes} min read</span>
                    <span className="note-card-arrow"><i data-lucide="arrow-up-right"></i></span>
                  </div>
                </div>
              </a>
            );
          }
          return (
            <a key={n.slug} className={'card note-card' + (i === tailIndex ? ' span-wide' : '')}
               href={'/notes/' + n.slug}>
              <div className="note-card-top">
                <span className={kindClass(n)}>{kindLabel(n)}</span>
                <span className="note-card-date">{formatDate(n.publishedAt)}</span>
              </div>
              <h3>{n.title}</h3>
              <p>{n.excerpt}</p>
              <div className="note-card-foot">
                <span className="note-card-tag">{tags[0]}</span>
                <span className="note-card-read"><i data-lucide="clock"></i> {n.readMinutes} min</span>
                <span className="note-card-arrow"><i data-lucide="arrow-up-right"></i></span>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

// ── StatusLog — pushed feed from /api/status/stream (admin-authored, newest first) ──
const slTime = (iso) => {
  const d = new Date(iso);
  return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
};
const slDayKey = (iso) => new Date(iso).toDateString();
const slDayLabel = (iso) =>
  // The site is English-only, so the locale is pinned: an undefined locale follows the browser,
  // which renders Czech weekday and month names for anyone whose browser is set to cs.
  new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

const SL_ARRIVE_MS = 900;   // must outlast the sl-arrive animation in frontend.css

// Lucide swaps every <i data-lucide> for an <svg> via replaceChild, which detaches the node
// React created. That is harmless while a list is rendered once, but this feed re-renders on
// every push: React would then write attribute updates to a node no longer in the document, so
// an edited icon would never change on screen. Handing the <i> over as raw innerHTML keeps it
// outside reconciliation — React only ever owns the wrapper span, and rewrites the whole thing
// when the name changes.
function SlIcon({ name }) {
  const safe = String(name || "circle").replace(/[^a-z0-9-]/gi, "") || "circle";
  return <span className="sl-ico" dangerouslySetInnerHTML={{ __html: '<i data-lucide="' + safe + '"></i>' }} />;
}

function StatusLog() {
  // Reuses the module-level settingsPromise, so this costs no extra request.
  const { email } = useSettings();
  const [clock, setClock] = useStateSec("");
  const [fresh, setFresh] = useStateSec([]);   // ids that landed after page load — these animate in
  const seen = React.useRef(null);             // null until the first frame arrives

  const live = useLive();
  const presence = usePresence();
  const messages = live.messages || [];

  useEffectSec(() => {
    // The first frame is the page loading, so nothing in it counts as new. Any later frame may
    // carry ids we have not shown yet, and those are the rows that get the arrival sweep.
    if (live.messages === null) return;

    if (seen.current === null) {
      seen.current = new Set(live.messages.map(m => m.id));
      return;
    }

    const arrived = live.messages.filter(m => !seen.current.has(m.id)).map(m => m.id);
    arrived.forEach(id => seen.current.add(id));
    if (arrived.length) setFresh(arrived);
  }, [live.messages]);

  // Clear the marks once the animation has played, so a later re-render does not replay it.
  useEffectSec(() => {
    if (!fresh.length) return;
    const id = setTimeout(() => setFresh([]), SL_ARRIVE_MS);
    return () => clearTimeout(id);
  }, [fresh]);

  React.useEffect(() => {
    const tick = () => {
      const d = new Date();
      setClock(String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0"));
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  // Every message is a feed row, newest first — the newest one gets no special treatment.
  const isFresh = (id) => fresh.indexOf(id) !== -1;
  const rows = [];
  let lastDay = new Date().toDateString();   // the live cursor line is "today", so today needs no separator
  messages.forEach((m) => {
    const day = slDayKey(m.createdAt);
    if (day !== lastDay) {
      lastDay = day;
      rows.push(<div className="sl-day" key={"d" + m.id}><span>{slDayLabel(m.createdAt)}</span></div>);
    }
    rows.push(
      // Keyed by id, never by index: rows are prepended live, and index keys would make React
      // recycle nodes across messages so the arrival sweep would play on the wrong line.
      <div className={"sl-line" + (isFresh(m.id) ? " sl-new" : "")} key={m.id}>
        <span className="t">[{slTime(m.createdAt)}]</span>
        <span className="ar">›</span>
        <SlIcon name={m.iconName} />
        <span dangerouslySetInnerHTML={{ __html: m.textHtml }} />
      </div>
    );
  });

  return (
    <section id="status" className="wrap reveal">
      <SectionHead ch="05" label="status"
        title="Live from the desk."
        lead="A read-only log — what I'm doing right now, and whether I'm reachable. No likes, no noise. Just signal." />
      <div className="card statuslog">
        <div className="sl-bar">
          <span className="sl-title">~/status.log</span>
          <span className="sl-live">
            <span className={"live-dot is-" + presence.state}></span> {presence.state}
            {presence.message ? " · " + presence.message : ""}
            <span className="sl-clock">{clock} local</span>
          </span>
        </div>
        <div className="sl-feed">
          <div className="sl-line live"><span className="t">[{clock}]</span><span className="ar">›</span><span><span className="sl-cur"></span></span></div>
          {rows}
          {messages.length === 0
            ? <div className="sl-empty">Nothing logged yet — but the desk is still here.</div>
            : null}
        </div>
        <div className="sl-foot">
          // read-only feed · <a href="/now">full log</a>
          {email ? <> · reply at <a href={'mailto:' + email}>{email}</a></> : null}
        </div>
      </div>
    </section>
  );
}

// ── Contact — email + links, all of it admin-editable ────────────────────
function Contact() {
  const { email, links } = useSettings();

  return (
    <section id="contact" className="wrap contact reveal">
      <div className="contact-inner">
        <SectionHead ch="06" label="contact"
          title={<>Let's <span className="accent">untangle</span> something together.</>}
          lead="I write, test, automate, and build systems. Reach out — about QA, tooling, or just to talk about working sustainably in tech." />
        <div className="contact-links">
          {/* Email keeps the primary-button treatment — that hierarchy is why it stays a
              first-class setting rather than just another row in the links table. */}
          {email ? <a className="btn btn-primary" href={'mailto:' + email}><i data-lucide="mail"></i> {email}</a> : null}
          {links.map(l => <ContactLinkAnchor key={l.id} link={l} className="contact-link" />)}
        </div>
      </div>
    </section>
  );
}

// ── Footer — same source as Contact, so the two cannot drift ─────────────
function Footer() {
  const { email, links } = useSettings();

  const explore = [["about","#about"],["focus","#focus"],["projects","#projects"],["notes","#notes"],["status","#status"],["contact","#contact"]];
  return (
    <footer className="footer">
      <div className="wrap footer-grid">
        <div className="f-brand">
          <a className="brand" href="#top"><span className="mark">s./</span><span className="wm">sirucka<span className="cz">.cz</span></span></a>
          <p>QA, automation, and the systems around them. I build tools that make complicated things manageable — and write about doing it sustainably.</p>
          <PresenceBadge className="f-status" />
        </div>
        <div className="f-col">
          <h4>Explore</h4>
          <nav>{explore.map(([l, h]) => <a key={h} href={h}>{l}</a>)}</nav>
        </div>
        <div className="f-col">
          <h4>Contact</h4>
          <nav>
            {email ? <a href={'mailto:' + email}><i data-lucide="mail"></i> {email}</a> : null}
            {links.map(l => <ContactLinkAnchor key={l.id} link={l} />)}
          </nav>
        </div>
      </div>
      <div className="wrap footer-bottom">
        <span className="fbrand">sirucka<span className="cz">.cz</span></span>
        {/* Mirrored in _Layout.cshtml's footer — keep the two in step. The cookies control is a
            button because it reopens the consent bar rather than navigating; the listener in
            cookie-consent.js is delegated, so it finds this even though React renders it late. */}
        <span className="footer-legal">
          <a href="/privacy">privacy</a>
          <button type="button" className="cookie-settings-link" data-cookie-settings>cookies</button>
        </span>
        <span>// building order from chaos · © 2026 · may the tests be with you</span>
      </div>
    </footer>
  );
}

export { About, Focus, Projects, Notes, StatusLog, Contact, Footer };
